import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Spin, Result, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, HomeOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG } from '../../config/api';

const MagicLinkCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      const redirect = '/';

      // 如果没有 token，则做会话检测（防止随意访问该路由导致空白页）
      if (!token) {
        try {
          const meResp = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/auth/me`, {
            method: 'GET',
            credentials: 'include'
          });
          if (meResp.ok) {
            navigate(redirect, { replace: true });
            return;
          }
        } catch (_) {}
        setStatus('error');
        setMessage('无效的验证链接');
        return;
      }

      try {
        const response = await fetch(
          `${API_CONFIG.TASK_MANAGER_BASE}/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          await getCurrentUser();
          setStatus('success');
          setMessage('登录成功！正在跳转到首页...');
          setTimeout(() => {
            navigate(redirect, { replace: true });
          }, 300);
          return;
        }

        // 处理非200响应
        let errorDetail = '';
        try {
          const errorData = await response.json();
          errorDetail = errorData?.detail || '';
        } catch (_) {
          // 无法解析JSON，忽略
        }

        // 如果令牌已被使用，通常说明会话已经建立，按成功处理
        const alreadyUsed = /已被?使用|already\s*used|token\s*used/i.test(errorDetail);
        if ((response.status === 400 || response.status === 409) && alreadyUsed) {
          await getCurrentUser();
          setStatus('success');
          setMessage('登录成功！正在跳转到首页...');
          setTimeout(() => {
            navigate(redirect, { replace: true });
          }, 300);
          return;
        }

        // 清除现有会话，避免用户误以为错误短码也能登录
        try { await logout(); } catch (_) {}
        setStatus('error');
        setMessage(errorDetail || '验证失败，请重新申请登录链接');
      } catch (error) {
        console.error('验证Magic Link失败:', error);
        setStatus('error');
        setMessage('网络错误，请稍后重试');
      }
    };

    verifyToken();
  }, [searchParams, navigate, getCurrentUser]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#ffffff',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderRadius: '12px',
          border: '1px solid #f0f0f0'
        }}
      >
        {status === 'loading' && (
          <div style={{ padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '20px', fontSize: '16px' }}>
              正在验证登录链接...
            </div>
          </div>
        )}

        {status === 'success' && (
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="登录成功！"
            subTitle={message}
            extra={[
              <Button
                key="home"
                type="primary"
                icon={<HomeOutlined />}
                onClick={() => navigate('/')}
                style={{
                  borderRadius: '8px',
                  height: '40px'
                }}
              >
                立即前往首页
              </Button>
            ]}
          />
        )}

        {status === 'error' && (
          <Result
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="验证失败"
            subTitle={message}
            extra={[
              <Button
                key="login"
                type="primary"
                onClick={() => navigate('/auth/login')}
                style={{
                  borderRadius: '8px',
                  height: '40px'
                }}
              >
                重新登录
              </Button>,
              <Button
                key="home"
                onClick={() => navigate('/')}
                style={{
                  borderRadius: '8px',
                  height: '40px'
                }}
              >
                返回首页
              </Button>
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default MagicLinkCallback;